import { useState, useEffect, useCallback } from 'react'
import { scanService, type ScanListItem } from '../services/scanService'

export function useScans(crisisId?: string, includeDeleted: boolean = false) {
  const [scans, setScans] = useState<ScanListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchScans = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (!crisisId) return
      const response = await scanService.getScans(crisisId, includeDeleted)
      setScans(response.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch scans'
      setError(errorMessage)
      console.error('Error fetching scans:', err)
    } finally {
      setIsLoading(false)
    }
  }, [crisisId, includeDeleted])

  const updateUnreadyScans = async () => {
    try {
      if (!crisisId) return
      const response = await scanService.getScans(crisisId, includeDeleted)
      const latestScans = response.data

      // Update only the scans that are currently unready
      setScans(prevScans =>
        prevScans.map(prevScan => {
          const latestScan = latestScans.find(s => s.id === prevScan.id)
          // Only update if this scan was unready and we have new data
          if (latestScan && prevScan.status !== 'ready') {
            return latestScan
          }
          return prevScan
        })
      )
    } catch (err) {
      console.error('Error updating unready scans:', err)
    }
  }

  useEffect(() => {
    fetchScans()
  }, [crisisId, fetchScans])

  return {
    scans,
    isLoading,
    error,
    refetch: fetchScans,
    updateUnreadyScans,
  }
}
